import { API_URL } from 'acscp/settings/appSettings';
import { ApiResponse, FatalError, processResponse, Success } from 'acscp/api/responses';
import * as t from 'io-ts';
import { objectToUrlEncodedString } from './formEncodedDataHelper';
import { RELEASE_VERSION } from 'acscp/settings/appSettings';

export enum Authentication {
    None = 0,
    SharingToken = 1,
    Login = 2
}

export interface AdditionalHeader {
    key: 'authorization' | 'X-Sharing-Tokens' | 'X-Preferred-Language';
    value: string;
}

export type ApiVersion = 'v1' | 'v2';

/**
 * Abstract generic base class for all API requests.
 * It constructs necessary request objects, fetches data from server, and handles all errors.
 * Note: type variable T is the type of the expected response object.
 */
export abstract class BaseRequest<T> {
    private resultDynamicType: t.Type<T>;
    private headers: {[name: string]: string} = {};
    private apiVersion: string;

    readonly type: string;
    readonly authenticationRequired: Authentication;

    constructor(
        public relativeUrl: string,
        public queryStringParameters: {[key: string]: string},
        resultDynamicType: t.Type<T>,
        authenticationRequired: Authentication = Authentication.Login,
        apiVersion: ApiVersion,
    ) {
        this.type = this.constructor.name;
        this.resultDynamicType = resultDynamicType;
        this.authenticationRequired = authenticationRequired;
        this.apiVersion = apiVersion;

        this.addHeader('Accept', 'application/json');
        this.addHeader('X-App-Version', RELEASE_VERSION);
    }

    protected addHeader(name: string, value: string): void {
        this.headers[name] = value;
    }

    execute = (additionalHeaders: AdditionalHeader[] = []) => {

        const responsePromise = fetch(this.buildRequestUrl(), this.buildRequestInit(additionalHeaders));
        return responsePromise.then(
            response => this.handleResponse(response),
            rejection => new FatalError('Network failure', rejection)
        );
    }

    private buildRequestUrl(): string {
        const path = `${API_URL}/${this.apiVersion}/${this.relativeUrl}`;
        const queryString = this.buildQueryString();

        return queryString.length === 0
            ? path
            : `${path}?${queryString}`;
    }

    private buildQueryString(): string {
        return objectToUrlEncodedString(this.queryStringParameters);
    }

    protected buildRequestInit(additionalHeaders: AdditionalHeader[]): RequestInit {
        return {
            headers: this.buildHeaders(additionalHeaders)
        };
    }

    private buildHeaders(additionalHeaders: AdditionalHeader[]): Headers {
        const headersObject = Object.keys(this.headers)
            .reduce(
                (acc, key) => {
                    acc.append(key, this.headers[key]);
                    return acc;
                },
                new Headers()
            );

        for (const header of additionalHeaders) {
            headersObject.append(header.key, header.value);
        }

        return headersObject;
    }

    protected handleResponse(response: Response): Promise<ApiResponse<T>> {
        if (this.resultDynamicType.name === t.undefined.name && response.ok) {
            const emptySuccessResponse = new Success<undefined>(undefined, response.headers);
            const typeCastedResponse = (emptySuccessResponse as any) as Success<T>;
            return Promise.resolve(typeCastedResponse);
        }
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            return Promise.resolve(new FatalError('Server didn\'t respond with JSON.', {}));
        }

        const bodyPromise = response.json();

        return bodyPromise.then(
            body => processResponse<T>(response, body, this.resultDynamicType),
            parsingFailure => new FatalError('Server responded with invalid JSON.', parsingFailure)
        );
    }
}

export interface RequestClass<TRequest extends BaseRequest<any>> {
    new (...args: any[]): TRequest;
}

export type RequestDescriptor<T> = BaseRequest<T> | RequestClass<BaseRequest<T>>;

export function getRequestType(requestDescriptor: BaseRequest<any> | RequestClass<any>): string {
    return requestDescriptor instanceof BaseRequest
        ? requestDescriptor.type
        : requestDescriptor.name;
}
